import { useState, useRef, useEffect, useId } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';

interface Option {
    id: string;
    name: string;
}

interface Props {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    className?: string;
    /** The trigger's id, so a `<label htmlFor>` can name it. */
    id?: string;
    /** Accessible name when no visible label points at the trigger. Defaults to the placeholder. */
    ariaLabel?: string;
}

/** How many options are rendered at most; the search narrows the rest. */
const MAX_SHOWN = 100;

/**
 * A select with a search box.
 *
 * Keyboard (design audit 2026-10-07, S1-09): the clear "×" is a button of its own beside the
 * trigger — it used to be a `span` inside it, reachable by mouse only, so a keyboard user could
 * set a filter and never clear it. The list is a `listbox`; ↓/↑ move through the options from
 * the search box, Enter picks, Esc closes and returns focus to the trigger.
 */
export function SearchableSelect({ options, value, onChange, placeholder, className, id, ariaLabel }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listId = useId();

    const selectedOption = options.find(o => o.id === value);

    const filtered = search
        ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
        : options;
    const shown = filtered.slice(0, MAX_SHOWN);

    const close = (refocus: boolean) => {
        setIsOpen(false);
        setSearch('');
        setActive(0);
        if (refocus) triggerRef.current?.focus();
    };

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) close(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    // The highlighted option stays in view while ↑/↓ move it.
    useEffect(() => {
        if (!isOpen) return;
        document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
    }, [isOpen, active, listId]);

    // Esc closes the list, not the dialog the select sits in.
    useTopmostEscape(isOpen, () => close(true));

    const pick = (o: Option) => {
        onChange(o.id);
        close(true);
    };

    const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, shown.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) pick(shown[active]); }
    };

    const name = ariaLabel ?? placeholder;
    // The trigger's name says what it is AND what is chosen ("Company: Dolder AG"); a label
    // alone replaced the button text, so the chosen value was never announced.
    const triggerName = `${name}: ${selectedOption ? selectedOption.name : 'none selected'}`;

    return (
        <div ref={ref} className={`relative ${className || ''}`}>
            <div className="flex w-full items-center rounded-md border bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500">
                <button
                    ref={triggerRef}
                    id={id}
                    type="button"
                    onClick={() => (isOpen ? close(false) : setIsOpen(true))}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listId : undefined}
                    aria-label={triggerName}
                    className="flex min-w-0 flex-1 items-center justify-between rounded-md px-3 py-1.5 text-left text-sm focus:outline-none"
                >
                    <span className={selectedOption ? 'truncate text-gray-900' : 'truncate text-gray-500'}>
                        {selectedOption ? selectedOption.name : placeholder}
                    </span>
                    <ChevronDown aria-hidden="true" className="ml-1 h-3.5 w-3.5 shrink-0 text-gray-500" />
                </button>
                {value && (
                    <button
                        type="button"
                        onClick={() => { onChange(''); setSearch(''); triggerRef.current?.focus(); }}
                        aria-label={`Clear ${name}`}
                        className="mr-1 shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border rounded-lg shadow-lg">
                    <div className="p-2 border-b">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setActive(0); }}
                            onKeyDown={onSearchKey}
                            placeholder="Search…"
                            role="combobox"
                            aria-expanded={true}
                            aria-autocomplete="list"
                            aria-label={`Search ${name}`}
                            aria-controls={listId}
                            aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
                            autoComplete="off"
                            className="w-full border rounded px-2 py-1 text-xs"
                        />
                    </div>
                    <div id={listId} role="listbox" aria-label={name} className="max-h-48 overflow-y-auto">
                        {filtered.length === 0 && (
                            <p className="text-xs text-gray-500 text-center py-3">No matches</p>
                        )}
                        {shown.map((o, i) => (
                            <div
                                key={o.id}
                                id={`${listId}-${i}`}
                                role="option"
                                aria-selected={o.id === value}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => pick(o)}
                                onMouseEnter={() => setActive(i)}
                                className={`cursor-pointer px-3 py-1.5 text-xs ${i === active ? 'bg-blue-50' : ''} ${o.id === value ? 'font-medium text-blue-800' : ''}`}
                            >
                                {o.name}
                            </div>
                        ))}
                        {filtered.length > MAX_SHOWN && (
                            <p className="text-[11px] text-gray-500 text-center py-1">Showing the first {MAX_SHOWN} of {filtered.length}…</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
